import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import queryString from 'query-string';
import { DesignLayoutType, EventPageType } from '@graphql/generated';
import { ColumnsV2, Flex, NoSsr } from '@withjoy/joykit';
import { useHistory } from '@react-router';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import { findEventPageByPageSlug } from '../layout.utils';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import { LayoutBaseProps } from '../layout.types';
import { StandaloneWebsiteHeader } from './components/StandaloneWebsiteHeader/StandaloneWebsiteHeader';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { useScrollToTop } from '@shared/components/ScrollToTop/ScrollToTop';
import { useFilterMigratedLegacyPages } from '../../hooks/useFilterMigratedLegacyPages';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import FooterNav from '../../components/FooterNav/FooterNav';
import { BrannanDesignProvider } from './BrannanDesignContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useMediaQuery } from '@withjoy/joykit/utils';

export interface LayoutBrannanProps extends Readonly<LayoutBaseProps> {
  setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
  isSinglePage?: boolean;
  setIsLastPageVisible?: (value: boolean) => void;
  setIsPastFirstPage?: (value: boolean) => void;
}

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

const LayoutBrannan: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme }) => {
  const { info, pages, eventDesign } = event;

  const history = useHistory();

  const isPreviewing = isInIframe();
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);

  const isSm2orLarge = useMediaQuery(theme => theme.mediaQueries.up({ breakpointAlias: 'sm2' }));
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });

  // Filter out legacy pages that have been migrated to custom pages
  const { filteredPages } = useFilterMigratedLegacyPages(pages);

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked } = useLayoutBrannanController(filteredPages);

  const { websiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });

  // Create eventProps with filtered pages for navigation menus (including hamburger menu)
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: filteredPages }), [event, filteredPages]);

  const { checkIsWelcomePage, loading: isWelcomePageCheckingLoading } = useWelcomePage();
  const { defaultPage, loading: isDefaultPageLoading } = useDefaultPage(filteredPages);

  // Determine if this is the root route (no slug in URL)
  const isRootRoute = !currentPageSlug;

  const { currentPage, isCustomPage } = useMemo(() => {
    // isCustomPageSlug is already aware of migrated pages when feature flag is ON (via useCurrentPageSlug)
    // For root route, use the default page (welcome page or first page)
    const currentPage = isRootRoute ? (defaultPage ?? null) : currentPageSlug ? findEventPageByPageSlug(filteredPages, currentPageSlug, isCustomPageSlug) : null;
    const isCustomPage = currentPage?.type === EventPageType.custom;
    return {
      currentPage,
      isCustomPage,
    };
  }, [currentPageSlug, isCustomPageSlug, filteredPages, isRootRoute, defaultPage]);

  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPage?.pageSlug);
  const { isWelcomePage } = useMemo(() => checkIsWelcomePage(currentPage?.pageSlug, currentPage?.type), [checkIsWelcomePage, currentPage?.pageSlug, currentPage?.type]);
  const eventPageRoute = useEventPageRoute(eventHandle, filteredPages);

  const queryParams = useQueryParams();

  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (currentPageSlug === 'registrylinks' || currentPageSlug === 'registry') {
      const path = queryString.stringifyUrl({
        url: `/${eventHandle}/registry`,
        query: queryParams,
      });
      window.location.href = path;
      return;
    }

    // Wait for feature flags to load before determining redirect
    // This prevents race conditions where defaultPage is undefined because flags are still loading
    if (isWelcomePageCheckingLoading || isDefaultPageLoading) {
      return;
    }

    // Don't redirect if user has already redirected
    if (hasRedirectedRef.current) {
      return;
    }

    // Don't redirect for root route - it will render the default page
    // Only redirect for invalid slugs (slug provided but page not found)
    if (!currentPage && !isRootRoute && defaultPage) {
      // Redirect to root URL (which will render the default page)
      const path = eventPageRoute.goToPath(defaultPage.type, defaultPage.pageSlug);

      if (path) {
        hasRedirectedRef.current = true;

        history.replace(
          queryString.stringifyUrl({
            url: path,
            query: queryParams,
          }),
        );
      }
    }
  }, [
    history,
    eventHandle,
    currentPage,
    currentPageSlug,
    queryParams,
    defaultPage,
    filteredPages,
    eventPageRoute,
    isWelcomePageCheckingLoading,
    isDefaultPageLoading,
    isRootRoute,
  ]);

  const { isSmallScreen, reapplyDesign, loading } = useBrannanDesign(eventHandle, eventDesign);

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, currentPage]);

  // Hide page photo for the welcome page when websiteHeader is present
  const shouldHidePagePhoto = isWelcomePage && websiteHeader;

  // To reduce the likelihood of preview falling out of sync in website designer,
  // we'll use the themeId as a key to force remount the Shell component
  const shellKey = isPreviewing ? event.eventDesign?.theme.themeId || 'default' : undefined;

  const shouldHideColumn = useMemo(() => {
    return isNativeAccommodationPage && !currentPage?.subTitle;
  }, [isNativeAccommodationPage, currentPage?.subTitle]);

  useScrollToTop({ behavior: 'instant' });

  return (
    <BrannanDesignProvider value={{ reapplyDesign }}>
      {/*  TODO: Question do we need to add !isNativeAccommodations check here - https://withjoy.atlassian.net/browse/PRNT-3157 */}
      {websiteHeader && (
        <StandaloneWebsiteHeader
          websiteHeader={websiteHeader}
          navBarVariant={isSmallScreen ? 'menu' : 'inline'}
          pages={filteredPages}
          currentPageType={currentPage?.type}
          eventHandle={eventHandle}
          handleMenuClicked={handleMenuClicked}
          eventProps={eventMenuProps}
          pageSlug={currentPage?.pageSlug}
          eventDesign={eventDesign}
        />
      )}
      <Shell
        eventHandle={eventHandle}
        eventDesign={event.eventDesign}
        isCustomPage={isCustomPage}
        key={shellKey}
      >
        {/*
            TODO: Remove this NoSsr (legacy decision)
          */}
        <NoSsr>
          {!isNativeAccommodationPage && (
            <WebsiteHeader
              data-testid={'layout-header'}
              websiteHeader={websiteHeader}
              applicatorProp={componentTitleBanner.prop}
              navBarVariant={isSmallScreen ? 'menu' : 'inline'}
              eventHandle={eventHandle}
              pages={filteredPages}
              info={info}
              handleMenuClicked={handleMenuClicked}
              handleEditEventClicked={handleEditEventClicked}
              eventProps={eventMenuProps}
              page={currentPage?.type}
              pageSlug={currentPage?.pageSlug}
              pageId={currentPage?.id}
              eventDisplayName={info?.eventDisplayName}
            />
          )}
          <Body
            data-testid={`layout-body-${currentPageSlug}`}
            marginTop={[0, null, 9]}
            page={currentPage}
            event={event}
            eventHandle={eventHandle}
            isWelcomePage={isWelcomePage}
            photo={
              isWelcomePage && event.video ? (
                <ColumnsV2.Column
                  style={isCustomPage ? { display: shouldHideColumn ? 'none' : 'block' } : {}}
                  span={[12, 10]}
                  offset={[0, 1]}
                >
                  <LazyWelcomeVideo
                    video={event.video}
                    thumbnailUrl={currentPage?.photoV2?.url || event.video.thumbnailUrl}
                  />
                </ColumnsV2.Column>
              ) : shouldHidePagePhoto ? null : (
                <CoverPhotoWrapper
                  renderFullColumn={currentPage?.photoPresentation?.presentationLayout !== 'center'}
                  isCustomPage={isCustomPage}
                  isNativeAccommodations={isNativeAccommodationPage}
                  subTitle={currentPage?.subTitle}
                >
                  <CoverPhoto
                    websiteLayoutType={DesignLayoutType.brannan}
                    photo={currentPage?.photoV2}
                    pageId={currentPage?.id}
                    pageType={currentPage?.type}
                    pageSlug={currentPage?.pageSlug}
                    photoPresentation={currentPage?.photoPresentation}
                  />
                </CoverPhotoWrapper>
              )
            }
          />
          <Flex __css={brannanFooterContainerStyles}>
            {/* FooterNav is only visible on mobile */}
            {isGuestMobileFooterNavEnabled && (
              <Flex
                width="100%"
                display={{ _: 'flex', sm2: 'none' }}
              >
                <FooterNav
                  pages={filteredPages}
                  eventHandle={eventHandle}
                  eventDisplayName={info?.eventDisplayName}
                  handleMenuClicked={handleMenuClicked}
                />
              </Flex>
            )}
            <Footer
              onButtonClicked={handleFooterButtonClicked}
              data-testid={'layout-footer'}
            />
          </Flex>
        </NoSsr>
      </Shell>
    </BrannanDesignProvider>
  );
};

LayoutBrannan.displayName = 'LayoutBrannan';

export { LayoutBrannan };
